/* eslint-disable @typescript-eslint/no-empty-function */
import {
  IGetBeneficiaryBatchHistoryRequest,
  IHistoryItem,
} from '@smiles/voebiz-core-beneficiaries/dist/Types/BeneficiaryBatchHistoryApi.types';
import { IDetailsList } from '@smiles/voebiz-core-beneficiaries/dist/Types/BeneficiaryFileDetailsApi.types';
import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getCreateBatchHistory, getFileImportDetails } from 'services/services';

export type BatchFileType = {
  id: string;
  filename: string;
  source: string;
  destination: string;
  created: string;
  status: string;
};

export interface ContextHistory {
  batchFiles: IHistoryItem[] | undefined;
  loading: boolean;
  isEmpty: boolean;
  isLastPage: boolean;
  isEmptyFilter: boolean;
  fileDetails?: IDetailsList[];
  selectedFile: BatchFileType;
  filter?: IGetBeneficiaryBatchHistoryRequest;
  setFilter: (filter: IGetBeneficiaryBatchHistoryRequest) => void;
  setFile: (file: BatchFileType) => void;
  showMore: () => void;
  refresh: () => void;
  setFileState: (file: BatchFileType) => void;
  tab: string;
  setTab: (file: string) => void;
  filterSearchInput: string;
  setFilterSearchInput: (file: string) => void;
}

const historyDefault = {
  batchFiles: [] as IHistoryItem[],
  fileDetails: [] as IDetailsList[],
  loading: false,
  isEmpty: false,
  isLastPage: false,
  isEmptyFilter: false,
  selectedFile: {} as BatchFileType,
  setFile: () => {},
  setFilter: () => {},
  refresh: () => {},
  showMore: () => {},
  setFileState: () => {},
  setTab: () => {},
  tab: '',
  filterSearchInput: '',
  setFilterSearchInput: () => {},
};

const HistoryBatchContext = createContext<ContextHistory>(historyDefault);

const HistoryBatchProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const [isLastPage, setIsLastPage] = useState(false);

  const [fileDetails, setFileDetails] = useState<IDetailsList[]>();

  const [batchFiles, setBatchFiles] = useState<IHistoryItem[]>([]);

  const params = new URLSearchParams(window.location.search);
  const active = params.get('active');
  const filename = params.get('filename');

  const tabType = active === 'desativacao' ? '_DS' : '_AT';

  const [tab, setTab] = useState<string>(tabType);

  const [filterSearchInput, setFilterSearchInput] = useState<string>('');

  const prevFilterSearchInput = useRef('');

  const [filter, setFilterState] = useState<IGetBeneficiaryBatchHistoryRequest>({
    sort: 'desc',
    sortBy: 'created',
    pageSize: 10,
    search: tabType,
  });

  const [selectedFile, setFileState] = useState<BatchFileType>();

  const setFilter = (newFilter: IGetBeneficiaryBatchHistoryRequest) => {
    setFilterState((prevFilter) => ({ ...prevFilter, ...newFilter }));
  };

  const showMore = useCallback(() => {
    const pageSize = Number(filter.pageSize ?? 10) + 25;

    const startRowNum = 0;

    setFilter({ pageSize, startRowNum });
  }, [filter.pageSize]);

  const getFileDetails = useCallback(async (file: BatchFileType) => {
    const destination = file?.destination?.split('/');

    const fileDestination = destination?.[destination?.length - 1];

    if (fileDestination) {
      const { data } = await getFileImportDetails(fileDestination);

      setFileDetails(data?.detailsList);
    }

    document.body.classList.remove('unscrollable');
  }, []);

  const setFile = useCallback(
    (file: BatchFileType) => {
      getFileDetails(file);
      setFileState(file);
    },
    [getFileDetails],
  );

  useEffect(() => {
    if (filterSearchInput.length > 0) {
      setFilter({ search: `${tab},${filterSearchInput}` });
    }

    if (prevFilterSearchInput.current.length > 1 && filterSearchInput === '') {
      setFilter({ search: `${tab},` });
    }

    prevFilterSearchInput.current = filterSearchInput;
  }, [filterSearchInput]);

  const getAllHistoryBath = useCallback(() => {
    setLoading(true);

    getCreateBatchHistory(filter)
      .then(({ data }) => {
        const response = data as { historyList: IHistoryItem[]; lastPage: boolean };

        if (filename?.length > 0) {
          const select = response.historyList.find((file) => file.filename === filename);
          if (select) {
            setFile(select);
          }
          params.delete('filename');
          window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
        }

        if (response) {
          setIsLastPage(response.lastPage);
          setBatchFiles(response.historyList);
        }
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    getAllHistoryBath();
  }, [getAllHistoryBath]);

  const isEmpty = !batchFiles || batchFiles.length === 0;

  const isEmptyFilter = isEmpty && filterSearchInput.length > 0;

  const refresh = getAllHistoryBath;

  const value = useMemo(
    () => ({
      batchFiles,
      loading,
      isLastPage,
      isEmpty,
      filter,
      setFilter,
      isEmptyFilter,
      showMore,
      refresh,
      selectedFile,
      setFile,
      fileDetails,
      setFileState,
      tab,
      setTab,
      filterSearchInput,
      setFilterSearchInput,
    }),
    [
      batchFiles,
      loading,
      isEmpty,
      filter,
      isLastPage,
      showMore,
      isEmptyFilter,
      refresh,
      selectedFile,
      fileDetails,
      setFile,
      setFileState,
      tab,
      setTab,
      filterSearchInput,
      setFilterSearchInput,
    ],
  );

  return <HistoryBatchContext.Provider value={value}>{children}</HistoryBatchContext.Provider>;
};

const useHistoryBatch = () => {
  const historyBatch = useContext(HistoryBatchContext);
  if (!historyBatch) {
    throw new Error('Cannot use `useHistoryBatch` outside of HistoryBatchProvider');
  }
  return historyBatch;
};

export { HistoryBatchContext, HistoryBatchProvider, useHistoryBatch };
